import React, { useState, useMemo, useEffect } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Box,
  Chip,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  IconButton,
  Tooltip,
  TablePagination,
  CircularProgress,
} from '@material-ui/core';
import { Skeleton } from '@material-ui/lab';
import {
  Visibility as ViewIcon,
  Search as SearchIcon,
  FilterList as FilterIcon,
} from '@material-ui/icons';
import { makeStyles } from '@material-ui/core/styles';
import { useQuery } from '@apollo/client';
import { AFFILIATE_CONVERSIONS_QUERY } from '../../core/graphql/queries/affiliate';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    backgroundColor: '#1a2332',
    border: '1px solid #333',
    borderRadius: 8,
  },
  table: {
    minWidth: 650,
  },
  tableHead: {
    backgroundColor: '#2d4a3e',
  },
  tableCell: {
    color: '#fff',
    borderBottom: '1px solid #333',
  },
  tableHeadCell: {
    color: '#4caf50',
    fontWeight: 'bold',
    borderBottom: '1px solid #333',
  },
  searchContainer: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: 'wrap',
  },
  searchField: {
    minWidth: 200,
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2d4a3e',
      color: '#fff !important',
      '& fieldset': {
        borderColor: '#333',
      },
      '&:hover fieldset': {
        borderColor: '#4caf50',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4caf50',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#fff !important',
    },
    '& .MuiOutlinedInput-input': {
      color: '#fff !important',
    },
    '& .MuiInputBase-input::placeholder': {
      color: '#fff !important',
      opacity: 0.7,
    },
    '& .MuiOutlinedInput-input::placeholder': {
      color: '#fff !important',
      opacity: 0.7,
    },
    '& input::placeholder': {
      color: '#fff !important',
      opacity: 0.7,
    },
  },
  filterControl: {
    minWidth: 120,
    '& .MuiOutlinedInput-root': {
      backgroundColor: '#2d4a3e',
      color: '#fff !important',
      '& fieldset': {
        borderColor: '#333',
      },
      '&:hover fieldset': {
        borderColor: '#4caf50',
      },
      '&.Mui-focused fieldset': {
        borderColor: '#4caf50',
      },
    },
    '& .MuiInputLabel-root': {
      color: '#fff !important',
    },
    '& .MuiSelect-root': {
      color: '#fff !important',
    },
    '& .MuiSelect-icon': {
      color: '#fff !important',
    },
    '& .MuiSelect-select': {
      color: '#fff !important',
    },
    '& .MuiInputBase-root': {
      color: '#fff !important',
    },
    '& .MuiMenuItem-root': {
      color: '#fff !important',
      backgroundColor: '#2d4a3e !important',
      '&:hover': {
        backgroundColor: '#4caf50 !important',
        color: '#fff !important',
      },
      '&.Mui-selected': {
        backgroundColor: '#4caf50 !important',
        color: '#fff !important',
      },
    },
    '& .MuiMenu-paper': {
      backgroundColor: '#2d4a3e !important',
      '& .MuiMenuItem-root': {
        color: '#fff !important',
        '&:hover': {
          backgroundColor: '#4caf50 !important',
        },
      },
    },
  },
  statusChip: {
    fontWeight: 'bold',
    '& .MuiChip-label': {
      color: '#fff !important',
    },
    '& span': {
      color: '#fff !important',
    },
  },
  pending: {
    backgroundColor: '#ff9800',
    color: '#fff !important',
    '& .MuiChip-label': {
      color: '#fff !important',
    },
  },
  approved: {
    backgroundColor: '#4caf50',
    color: '#fff !important',
    '& .MuiChip-label': {
      color: '#fff !important',
    },
  },
  paid: {
    backgroundColor: '#2196f3',
    color: '#fff !important',
    '& .MuiChip-label': {
      color: '#fff !important',
    },
  },
  rejected: {
    backgroundColor: '#f44336',
    color: '#fff !important',
    '& .MuiChip-label': {
      color: '#fff !important',
    },
  },
  actionButton: {
    color: '#4caf50',
    '&:hover': {
      backgroundColor: 'rgba(76, 175, 80, 0.1)',
    },
  },
  emptyState: {
    textAlign: 'center',
    padding: theme.spacing(4),
    color: '#ccc',
  },
  loadingContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: 200,
  },
  pagination: {
    '& .MuiTablePagination-root': {
      color: '#fff !important',
      backgroundColor: '#1a2332',
    },
    '& .MuiTablePagination-select': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-selectIcon': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-displayedRows': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-selectLabel': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-actions': {
      '& .MuiIconButton-root': {
        color: '#fff !important',
        '&:hover': {
          backgroundColor: 'rgba(255, 255, 255, 0.1)',
        },
      },
    },
    '& .MuiTablePagination-toolbar': {
      color: '#fff !important',
    },
    '& .MuiSelect-select': {
      color: '#fff !important',
    },
    '& .MuiSelect-icon': {
      color: '#fff !important',
    },
    '& .MuiInputBase-root': {
      color: '#fff !important',
    },
    '& .MuiInputBase-input': {
      color: '#fff !important',
    },
    '& .MuiFormControl-root': {
      color: '#fff !important',
    },
    '& *': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select, & .MuiTablePagination-actions .MuiIconButton-root': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-selectLabel::after': {
      color: '#fff !important',
    },
    '& .MuiTablePagination-selectLabel::before': {
      color: '#fff !important',
    },
  },
}));

interface Conversion {
  id: string;
  affiliateId: string;
  referralCode: string;
  artistId: string;
  artist: {
    id: string;
    email: string;
    firstName?: string;
    lastName?: string;
  };
  commissionAmount: number;
  status: 'pending' | 'approved' | 'paid' | 'rejected';
  approvedAt?: string;
  paidAt?: string;
  rejectedAt?: string;
  rejectionReason?: string;
  createdAt: string;
  updatedAt: string;
}

interface ConversionsTableProps {
  onViewConversion?: (conversion: Conversion) => void;
}

const ConversionsTable: React.FC<ConversionsTableProps> = ({ onViewConversion }) => {
  const classes = useStyles();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data, loading, error, refetch } = useQuery(AFFILIATE_CONVERSIONS_QUERY, {
    variables: {
      limit: rowsPerPage,
      offset: page * rowsPerPage,
      ...(statusFilter !== 'all' && { status: statusFilter as 'pending' | 'approved' | 'paid' | 'rejected' }),
    },
    errorPolicy: 'all',
  });

  // Debug logging
  React.useEffect(() => {
    if (data) {
      console.log('ConversionsTable: Data received:', data);
    }
    if (error) {
      console.error('ConversionsTable: Error:', error);
      console.error('ConversionsTable: Error details:', {
        message: error.message,
        graphQLErrors: error.graphQLErrors,
        networkError: error.networkError,
      });
    }
  }, [data, error]);

  const conversions = (data?.conversions || []).filter((conv: Conversion | null) => conv !== null && conv !== undefined);
  const totalCount = conversions.length;

  console.log('ConversionsTable: Conversions count:', conversions.length, 'Loading:', loading, 'Error:', error?.message);

  // Refetch when status filter changes
  useEffect(() => {
    refetch();
  }, [statusFilter, refetch]);

  const filteredConversions = useMemo(() => {
    return conversions.filter((conversion: Conversion) => {
      if (!conversion || !conversion.referralCode) return false;
      const matchesSearch = conversion.referralCode
        .toLowerCase()
        .includes(searchTerm.toLowerCase());
      return matchesSearch;
    });
  }, [conversions, searchTerm]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const getStatusChip = (status: string) => {
    const statusConfig = {
      pending: { label: 'Pending', className: classes.pending },
      approved: { label: 'Approved', className: classes.approved },
      paid: { label: 'Paid', className: classes.paid },
      rejected: { label: 'Rejected', className: classes.rejected },
    };

    const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.pending;
    
    return (
      <Chip
        label={config.label}
        className={`${classes.statusChip} ${config.className}`}
        size="small"
        style={{ 
          color: '#fff',
          '--chip-label-color': '#fff'
        } as React.CSSProperties}
      />
    );
  };

  if (error) {
    return (
      <Box className={classes.emptyState}>
        <Typography variant="h6" gutterBottom>
          Error Loading Conversions
        </Typography>
        <Typography variant="body2">
          Unable to load conversions data. Please try again later.
        </Typography>
      </Box>
    );
  }

  return (
    <Box>
      <Box className={classes.searchContainer}>
        <TextField
          placeholder="Search by artist name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className={classes.searchField}
          style={{ 
            color: '#fff',
          }}
          InputProps={{
            startAdornment: <SearchIcon style={{ color: '#fff', marginRight: 8 }} />,
            style: { color: '#fff' }
          }}
          inputProps={{
            style: { color: '#fff' }
          }}
        />
        
        <FormControl className={classes.filterControl}>
          <InputLabel style={{ color: '#fff' }}>Status</InputLabel>
          <Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value as string)}
            style={{ color: '#fff' }}
            MenuProps={{
              PaperProps: {
                style: {
                  backgroundColor: '#2d4a3e',
                  color: '#fff',
                  border: '1px solid #333'
                }
              },
              anchorOrigin: {
                vertical: 'bottom',
                horizontal: 'left',
              },
              transformOrigin: {
                vertical: 'top',
                horizontal: 'left',
              },
            }}
          >
            <MenuItem value="all" style={{ color: '#fff', backgroundColor: '#2d4a3e' }}>All Status</MenuItem>
            <MenuItem value="pending" style={{ color: '#fff', backgroundColor: '#2d4a3e' }}>Pending</MenuItem>
            <MenuItem value="approved" style={{ color: '#fff', backgroundColor: '#2d4a3e' }}>Approved</MenuItem>
            <MenuItem value="paid" style={{ color: '#fff', backgroundColor: '#2d4a3e' }}>Paid</MenuItem>
            <MenuItem value="rejected" style={{ color: '#fff', backgroundColor: '#2d4a3e' }}>Rejected</MenuItem>
          </Select>
        </FormControl>
      </Box>

      <TableContainer component={Paper} className={classes.tableContainer}>
        <Table className={classes.table} aria-label="conversions table">
          <TableHead className={classes.tableHead}>
            <TableRow>
              <TableCell className={classes.tableHeadCell}>Referral Code</TableCell>
              <TableCell className={classes.tableHeadCell}>Artist</TableCell>
              <TableCell className={classes.tableHeadCell}>Commission</TableCell>
              <TableCell className={classes.tableHeadCell}>Status</TableCell>
              <TableCell className={classes.tableHeadCell}>Created Date</TableCell>
              <TableCell className={classes.tableHeadCell}>Approved Date</TableCell>
              <TableCell className={classes.tableHeadCell}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: rowsPerPage }).map((_, index) => (
                <TableRow key={index}>
                  {Array.from({ length: 8 }).map((_, cellIndex) => (
                    <TableCell key={cellIndex} className={classes.tableCell}>
                      <Skeleton variant="text" />
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : filteredConversions.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className={classes.emptyState}>
                  <Typography variant="h6" gutterBottom>
                    No Conversions Found
                  </Typography>
                  <Typography variant="body2">
                    {searchTerm || statusFilter !== 'all'
                      ? 'No conversions match your current filters.'
                      : 'You haven\'t generated any conversions yet.'}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              filteredConversions
                .filter((conv: Conversion) => conv && conv.id) // Additional safety filter
                .map((conversion: Conversion) => (
                <TableRow key={conversion?.id || Math.random()} hover>
                  <TableCell className={classes.tableCell}>
                    {conversion?.referralCode || 'N/A'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {conversion?.artist ? (
                      conversion.artist.firstName && conversion.artist.lastName 
                        ? `${conversion.artist.firstName} ${conversion.artist.lastName}`
                        : conversion.artist.email || 'Unknown Artist'
                    ) : (
                      'Unknown Artist'
                    )}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {formatCurrency(conversion?.commissionAmount || 0)}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {getStatusChip(conversion?.status || 'pending')}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {conversion?.createdAt ? formatDate(conversion.createdAt) : '-'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    {conversion?.approvedAt ? formatDate(conversion.approvedAt) : '-'}
                  </TableCell>
                  <TableCell className={classes.tableCell}>
                    <Tooltip title="View Details">
                      <IconButton
                        className={classes.actionButton}
                        onClick={() => onViewConversion?.(conversion)}
                      >
                        <ViewIcon />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {!loading && (
        <TablePagination
          className={classes.pagination}
          style={{ 
            backgroundColor: '#1a2332',
            color: '#fff',
            border: '1px solid #333',
            borderRadius: '8px',
            marginTop: '16px'
          }}
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      )}
    </Box>
  );
};

export default ConversionsTable;
